// frontend/src/super-admin/billing/BillingReportsPage.tsx
//
// Admin "Billing Reports" page: platform-wide revenue/outstanding stat cards,
// a 12-month revenue bar chart, an invoices table (status filter + pagination
// + CSV export), and a per-tenant billing breakdown table.
//
// All data comes from GET /api/admin/billing/reports/* (billing.read).
import * as React from 'react';
import { toast } from 'sonner';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { DataTable } from '../../design-system/components/DataTable';
import { EmptyState } from '../../design-system/components/EmptyState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { PageHeader } from '../../design-system/components/PageHeader';
import { useApi } from '../../design-system/useApi';
import { formatMoney, formatDate } from '../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../design-system/visual';
import { getToken } from '../../core/api-client';

interface ReportsSummary {
  revenue_this_month: string;
  revenue_last_month: string;
  outstanding_total: string;
  overdue_count: number;
  dunning_count: number;
  active_tenants: number;
  trial_tenants: number;
}

interface RevenueMonth { month: string; total: string; }

type ReportInvoiceStatus = 'open' | 'paid' | 'void' | 'uncollectible';

interface ReportInvoice {
  id: string;
  invoice_number: string;
  tenant_id: string;
  tenant_name: string | null;
  status: ReportInvoiceStatus;
  total: string;
  currency: string;
  issued_at: string;
  due_at: string | null;
  paid_at: string | null;
  dunning_attempts: number;
}

interface ReportTenant {
  tenant_id: string;
  slug: string;
  name: string;
  subscription_status: string;
  seat_count: number;
  module_count: number;
  lifetime_paid: string;
  outstanding: string;
  adjustments_total: string;
}

const INVOICE_STATUS_OPTIONS: Array<{ value: 'all' | ReportInvoiceStatus; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'uncollectible', label: 'Uncollectible' },
];

const PAGE_SIZE = 25;
const REVENUE_MONTHS = 12;

/** Anchor the 12-month window on today, zero-filling months absent from the API. */
export function buildRevenueSeries(rows: RevenueMonth[], count = REVENUE_MONTHS): Array<{ month: string; total: number }> {
  const byMonth = new Map(rows.map((r) => [r.month, Number(r.total)]));
  const now = new Date();
  const out: Array<{ month: string; total: number }> = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ month: key, total: byMonth.get(key) ?? 0 });
  }
  return out;
}

function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short' });
}

export function BillingReportsPage() {
  const summaryQ = useApi<{ summary: ReportsSummary }>('/api/admin/billing/reports/summary');
  const revenueQ = useApi<{ revenue: RevenueMonth[] }>(`/api/admin/billing/reports/revenue?months=${REVENUE_MONTHS}`);
  const tenantsQ = useApi<{ tenants: ReportTenant[] }>('/api/admin/billing/reports/tenants');

  const [statusFilter, setStatusFilter] = React.useState<'all' | ReportInvoiceStatus>('all');
  const [page, setPage] = React.useState(1);

  const invoicesPath = React.useMemo(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    params.set('page', String(page));
    params.set('page_size', String(PAGE_SIZE));
    return `/api/admin/billing/reports/invoices?${params.toString()}`;
  }, [statusFilter, page]);
  const invoicesQ = useApi<{ rows: ReportInvoice[]; total_count: number }>(invoicesPath, [invoicesPath]);

  // Reset to page 1 whenever the filter changes.
  React.useEffect(() => { setPage(1); }, [statusFilter]);

  const [downloading, setDownloading] = React.useState(false);
  async function downloadCsv() {
    setDownloading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const qs = params.toString();
      const url = `/api/admin/billing/reports/invoices.csv${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = statusFilter === 'all' ? 'invoices.csv' : `invoices-${statusFilter}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err: any) {
      toast.error('Could not download CSV', { description: err.message });
    } finally {
      setDownloading(false);
    }
  }

  const summary = summaryQ.data?.summary;
  const revenueSeries = React.useMemo(() => buildRevenueSeries(revenueQ.data?.revenue ?? []), [revenueQ.data]);
  const invoiceRows = invoicesQ.data?.rows ?? [];
  const totalCount = invoicesQ.data?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const tenantRows = tenantsQ.data?.tenants ?? [];

  return (
    <div>
      <PageHeader
        eyebrow="Platform / Billing"
        title="Billing Reports"
        description="Revenue, outstanding balances, and per-tenant billing across the whole platform."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {summaryQ.error ? (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ErrorState title="Couldn't load billing summary" description={summaryQ.error} onRetry={summaryQ.reload} />
          </section>
        ) : (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label="Revenue this month"
              value={summary ? formatMoney(summary.revenue_this_month, 'USD') : '—'}
              sub={summary ? `${formatMoney(summary.revenue_last_month, 'USD')} last month` : undefined}
            />
            <StatCard
              label="Outstanding"
              value={summary ? formatMoney(summary.outstanding_total, 'USD') : '—'}
            />
            <StatCard
              label="Overdue"
              value={summary ? String(summary.overdue_count) : '—'}
              sub={summary ? `${summary.dunning_count} in dunning` : undefined}
              tone="warning"
            />
            <StatCard
              label="Active tenants"
              value={summary ? String(summary.active_tenants) : '—'}
              sub={summary ? `${summary.trial_tenants} trial${summary.trial_tenants === 1 ? '' : 's'}` : undefined}
              tone="success"
            />
          </section>
        )}

        {/* Revenue chart */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Revenue — last 12 months</h3>
          <RevenueChart data={revenueSeries} />
        </section>

        {/* Invoices */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className={VISUAL.dashSection}>Invoices</h3>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                <SelectTrigger
                  aria-label="Invoice status"
                  className="h-10 rounded-full bg-card/80 border-border/60 px-4 text-[12.5px] font-semibold w-auto min-w-[160px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVOICE_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="rounded-full h-10 px-4 text-[12.5px] font-semibold gap-1.5"
                disabled={downloading}
                onClick={downloadCsv}
              >
                <Download className="h-3.5 w-3.5" />
                {downloading ? 'Preparing…' : 'Download CSV'}
              </Button>
            </div>
          </div>

          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<ReportInvoice>
              loading={invoicesQ.loading}
              rows={invoiceRows}
              rowKey={(r) => r.id}
              columns={[
                { key: 'number', header: 'Invoice #', cell: (r) => <span className="font-mono text-[12.5px]">{r.invoice_number}</span> },
                { key: 'tenant', header: 'Tenant', cell: (r) => r.tenant_name
                  ? <span className="text-[13px] font-medium">{r.tenant_name}</span>
                  : <span className="font-mono text-[12.5px]">{r.tenant_id.slice(0, 8)}…</span> },
                { key: 'status', header: 'Status', cell: (r) => invoiceStatusBadge(r.status) },
                { key: 'total', header: 'Total', cell: (r) => <span className="font-mono tabular-nums font-semibold">{formatMoney(r.total, r.currency)}</span> },
                { key: 'issued', header: 'Issued', cell: (r) => formatDate(r.issued_at), className: 'text-xs text-muted-foreground' },
                { key: 'due', header: 'Due', cell: (r) => formatDate(r.due_at), className: 'text-xs text-muted-foreground' },
                { key: 'paid', header: 'Paid', cell: (r) => formatDate(r.paid_at), className: 'text-xs text-muted-foreground' },
                { key: 'dunning', header: 'Dunning', cell: (r) => r.dunning_attempts, className: 'text-xs text-muted-foreground' },
              ]}
              emptyState={
                invoicesQ.error
                  ? <ErrorState title="Couldn't load invoices" description={invoicesQ.error} onRetry={invoicesQ.reload} />
                  : <EmptyState title="No invoices" description="No platform invoices match the current filter." />
              }
            />
          </div>

          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
              <span className="tabular-nums">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="outline" className="rounded-full h-9 gap-1"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <span className="tabular-nums px-1">Page {page} of {pageCount}</span>
                <Button
                  size="sm" variant="outline" className="rounded-full h-9 gap-1"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Tenants breakdown */}
        <section>
          <h3 className={[VISUAL.dashSection, 'mb-3'].join(' ')}>Tenants</h3>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<ReportTenant>
              loading={tenantsQ.loading}
              rows={tenantRows}
              rowKey={(r) => r.tenant_id}
              columns={[
                { key: 'name', header: 'Tenant', cell: (r) => (
                  <div>
                    <div className="text-[13px] font-medium">{r.name}</div>
                    <div className="text-[11.5px] font-mono text-muted-foreground">{r.slug}</div>
                  </div>
                ) },
                { key: 'status', header: 'Status', cell: (r) => tenantStatusBadge(r.subscription_status) },
                { key: 'seats', header: 'Seats', cell: (r) => r.seat_count, className: 'tabular-nums' },
                { key: 'modules', header: 'Modules', cell: (r) => r.module_count, className: 'tabular-nums' },
                { key: 'lifetime', header: 'Lifetime paid', cell: (r) => <span className="font-mono tabular-nums">{formatMoney(r.lifetime_paid, 'USD')}</span> },
                { key: 'outstanding', header: 'Outstanding', cell: (r) => <span className="font-mono tabular-nums">{formatMoney(r.outstanding, 'USD')}</span> },
                { key: 'adjustments', header: 'Adjustments', cell: (r) => <span className="font-mono tabular-nums">{formatMoney(r.adjustments_total, 'USD')}</span> },
              ]}
              emptyState={
                tenantsQ.error
                  ? <ErrorState title="Couldn't load tenants" description={tenantsQ.error} onRetry={tenantsQ.reload} />
                  : <EmptyState title="No tenants" description="No tenants to report on yet." />
              }
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone = 'neutral' }: {
  label: string; value: string; sub?: string; tone?: 'neutral' | 'success' | 'warning';
}) {
  const dotClass =
    tone === 'success' ? 'bg-success' :
    tone === 'warning' ? 'bg-warning' :
    'bg-foreground/40';
  return (
    <div className={[VISUAL.bento, 'p-4'].join(' ')}>
      <div className="flex items-center justify-between mb-2">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span aria-hidden className={['size-2 rounded-full', dotClass].join(' ')} />
      </div>
      <p className="font-sans font-light text-[1.8rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      {sub && <p className="text-[12px] text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
}

function RevenueChart({ data }: { data: Array<{ month: string; total: number }> }) {
  const width = 720;
  const chartHeight = 160;
  const labelHeight = 28;
  const height = chartHeight + labelHeight;
  const gap = 8;
  const barWidth = data.length > 0 ? (width - gap * (data.length - 1)) / data.length : 0;
  const max = Math.max(1, ...data.map((d) => d.total));

  return (
    <svg
      role="img"
      aria-label="Revenue by month, last 12 months"
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto"
    >
      <title>Revenue by month, last 12 months</title>
      {data.map((d, i) => {
        const h = (d.total / max) * chartHeight;
        const x = i * (barWidth + gap);
        const y = chartHeight - h;
        return (
          <g key={d.month}>
            <rect
              data-testid="revenue-bar"
              x={x}
              y={y}
              width={barWidth}
              height={Math.max(h, 0)}
              rx={3}
              className="fill-saffron"
            />
            <text
              x={x + barWidth / 2}
              y={chartHeight + 18}
              textAnchor="middle"
              className="fill-muted-foreground"
              style={{ fontSize: 9 }}
            >
              {monthLabel(d.month)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function invoiceStatusBadge(s: ReportInvoiceStatus): React.ReactNode {
  const tone: StatusTone =
    s === 'paid' ? 'success' :
    s === 'open' ? 'warning' :
    s === 'void' ? 'muted' :
    'destructive';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s}
    </span>
  );
}

function tenantStatusBadge(status: string): React.ReactNode {
  const tone: StatusTone =
    status === 'active' ? 'success' :
    status === 'trial' ? 'info' :
    status === 'past_due' ? 'warning' :
    status === 'read_only' ? 'warning' :
    status === 'suspended' ? 'destructive' :
    'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {status.replace('_', ' ')}
    </span>
  );
}
